import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { clsx } from "clsx";
import type { ChatReactionGroup, ChatUserBrief } from "../types";
import { Avatar } from "./Avatar";

interface ReactionsModalProps {
  reactions: ChatReactionGroup[];
  members: ChatUserBrief[];
  currentUserId: string | undefined;
  initialEmoji?: string;
  onClose: () => void;
}

export function ReactionsModal({
  reactions,
  members,
  currentUserId,
  initialEmoji,
  onClose,
}: ReactionsModalProps) {
  const [activeEmoji, setActiveEmoji] = useState<string>(
    initialEmoji && reactions.some((r) => r.emoji === initialEmoji)
      ? initialEmoji
      : (reactions[0]?.emoji ?? ""),
  );

  const membersById = useMemo(() => {
    const map = new Map<string, ChatUserBrief>();
    for (const m of members) map.set(m.id, m);
    return map;
  }, [members]);

  const totalCount = reactions.reduce((a, r) => a + r.count, 0);
  const activeGroup = reactions.find((r) => r.emoji === activeEmoji);

  const visible: { emoji: string; uid: string }[] =
    activeEmoji === "__all__"
      ? reactions.flatMap((g) => g.user_ids.map((uid) => ({ emoji: g.emoji, uid })))
      : (activeGroup?.user_ids ?? []).map((uid) => ({
          emoji: activeGroup!.emoji,
          uid,
        }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-gray-200 rounded-md w-full max-w-sm shadow-xl flex flex-col max-h-[70vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h3 className="text-sm font-mono font-semibold">Reakcje</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveEmoji("__all__")}
            className={clsx(
              "px-3 py-2 text-sm flex items-center gap-1 border-b-2 whitespace-nowrap",
              activeEmoji === "__all__"
                ? "border-red-500 text-red-600 font-semibold"
                : "border-transparent text-gray-700",
            )}
          >
            Wszystkie <span className="text-gray-500">{totalCount}</span>
          </button>
          {reactions.map((g) => (
            <button
              key={g.emoji}
              type="button"
              onClick={() => setActiveEmoji(g.emoji)}
              className={clsx(
                "px-3 py-2 text-sm flex items-center gap-1 border-b-2 whitespace-nowrap",
                activeEmoji === g.emoji
                  ? "border-red-500 text-red-600 font-semibold"
                  : "border-transparent text-gray-700 hover:bg-gray-50",
              )}
            >
              <span className="text-base leading-none">{g.emoji}</span>
              <span className="text-gray-500">{g.count}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.map((entry, idx) => {
            const u = membersById.get(entry.uid);
            const isSelf = entry.uid === currentUserId;
            const name =
              u?.full_name || u?.username || "(użytkownik usunięty)";
            return (
              <div
                key={`${entry.emoji}-${entry.uid}-${idx}`}
                className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0"
              >
                <Avatar user={u ?? null} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">
                    {isSelf ? "Ty" : name}
                  </div>
                  {u?.username && !isSelf && (
                    <div className="text-[11px] text-gray-500 truncate">
                      {u.username}
                    </div>
                  )}
                </div>
                <span className="text-xl leading-none">{entry.emoji}</span>
              </div>
            );
          })}
          {reactions.length === 0 && (
            <div className="p-3 text-xs text-gray-500">Brak reakcji.</div>
          )}
        </div>
      </div>
    </div>
  );
}
